'use client';

import { useEffect } from 'react';

const KEYBOARD_OFFSET_VAR = '--kb-offset';
const KEYBOARD_OPEN_THRESHOLD = 16;

function setKeyboardState(isOpen: boolean, offset: number) {
  const root = document.documentElement;
  root.classList.toggle('kb-open', isOpen);
  root.style.setProperty(KEYBOARD_OFFSET_VAR, `${Math.max(0, Math.round(offset))}px`);

  // Notify interested listeners (e.g. BottomNav controllers) about keyboard changes
  if (typeof window !== 'undefined' && 'CustomEvent' in window) {
    const event = new CustomEvent('keyboardchange', {
      detail: { isOpen, offset },
    });
    window.dispatchEvent(event);
  }
}

function addVisualViewportListeners(apply: () => void, vv: VisualViewport) {
  vv.addEventListener('resize', apply);
  vv.addEventListener('scroll', apply);
  window.addEventListener('orientationchange', apply);

  return () => {
    vv.removeEventListener('resize', apply);
    vv.removeEventListener('scroll', apply);
    window.removeEventListener('orientationchange', apply);
  };
}

function addFocusFallbackListeners(onFocusIn: (event: FocusEvent) => void, onFocusOut: () => void) {
  window.addEventListener('focusin', onFocusIn);
  window.addEventListener('focusout', onFocusOut);

  return () => {
    window.removeEventListener('focusin', onFocusIn);
    window.removeEventListener('focusout', onFocusOut);
  };
}

export function KeyboardViewport() {
  useEffect(() => {
    const vv = window.visualViewport;

    if (vv) {
      const apply = () => {
        const bottomInset = window.innerHeight - vv.height - vv.offsetTop;
        const active = document.activeElement as HTMLElement | null;
        const isInputLike =
          !!active &&
          (active.tagName === 'INPUT' ||
            active.tagName === 'TEXTAREA' ||
            active.getAttribute('contenteditable') === 'true');

        // Only treat visualViewport insets as keyboard when an input is actually focused.
        // This prevents false positives (notably when DevTools is open) from creating huge bottom padding.
        const isOpen = isInputLike && bottomInset > KEYBOARD_OPEN_THRESHOLD;
        setKeyboardState(isOpen, isOpen ? bottomInset : 0);
      };

      apply();
      return addVisualViewportListeners(apply, vv);
    }

    const focusIn = (event: FocusEvent) => {
      const target = event.target as HTMLElement | null;
      if (!target) return;

      const isInputLike =
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.getAttribute('contenteditable') === 'true';

      if (isInputLike) {
        setKeyboardState(true, 280);
      }
    };

    const focusOut = () => setKeyboardState(false, 0);

    return addFocusFallbackListeners(focusIn, focusOut);
  }, []);

  return null;
}
